<script setup>
import NavBar from './../components/NavBar.vue'
import {nextTick, onMounted, onUnmounted, reactive, ref, watch} from 'vue';
import { Collapse } from 'vue-collapsed'
import io from 'socket.io-client';
import auth from '../auth/auth.js'
import { useRoute } from 'vue-router';

const API_BASE = process?.env?.VUE_APP_API_BASE_URL ?? '/api';

const route = useRoute();
const socket = ref(null);
const messages = ref([]);
const messageInput = ref('');
let senderId = 0;
let recipientId = 0;
const currentRecipientId = ref(0);
const currentRecipientName = ref("Select a user from the sidebar");
const contacts = ref([]);

const messageItemsMap = ref(new Map());
const messageScrollableArea = ref(null);

// Watch for route query changes (when navigating from CounselorCard)
watch(() => route.query, async (newQuery) => {
  if (newQuery.recipient_id && newQuery.name) {
    await handleIncomingRecipient(newQuery.recipient_id, newQuery.name);
  }
}, { immediate: false });

async function handleIncomingRecipient(newRecipientId, name) {
  recipientId = Number(newRecipientId);
  currentRecipientId.value = Number(newRecipientId);
  currentRecipientName.value = name;
  
  // Load message history for this recipient
  await loadMessageHistory();
  
  // Refresh contacts list to include this recipient if not already there
  await fetchMessageItems();
  contacts.value = Array.from(messageItemsMap.value.values());
  sortContacts();
  
  await nextTick(() => {
    scrollToBottom();
  });
}

//initialize connection
onMounted(async() => {
  const loggedIn = await auth.isAuthenticated();
  if (!loggedIn) {
    console.log("User not logged in");
    return;
  }

  senderId = auth.getUserID();

  await fetchMessageItems();

  // Check if we have recipient info from query params (from CounselorCard navigation)
  if (route.query.recipient_id && route.query.name) {
    await handleIncomingRecipient(route.query.recipient_id, route.query.name);
  }

  const SOCKET_URL = process?.env?.VUE_APP_SOCKET_URL ?? window.location.origin;
  socket.value = io(SOCKET_URL, {
    path: '/socket.io',
    transports: ['websocket', 'polling'],
    withCredentials: true,
  });

  socket.value.on('connect', () => {
    socket.value.emit('user_login', senderId);
    socket.value.emit('get_contacts', senderId);
  });

  socket.value.on('contact_info', (contactInfo) => {
    contacts.value = contactInfo;
  })

  socket.value.on('new_message', async (data) => {
    console.log("RECEIVED NEW MESSAGE")
    messages.value.push({
      ...data,
      isSentByCurrentUser: data.sender_id === senderId,
    });

    const otherUserId = data.sender_id === senderId ? data.recipient_id : data.sender_id;
    const otherUserName = recipientId === otherUserId ? currentRecipientName.value : 'Unknown';
    const newMessage = data.message;
    const newTime = data.timestamp;

    let item = messageItemsMap.value.get(otherUserId);

    if (item) {
      item.lastMessage = newMessage;
      item.timestamp = newTime;

      // increase unread ONLY if user is not actively chatting with them
      if (currentRecipientId.value !== otherUserId) {
        item.unreadCount = (item.unreadCount || 0) + 1;
      }
    } else {
      messageItemsMap.value.set(otherUserId, {
        userId: otherUserId,
        name: otherUserName,
        lastMessage: newMessage,
        timestamp: newTime,
        profilePic: '',
        unreadCount: currentRecipientId.value === otherUserId ? 0 : 1
      });
    }

    await updateMessageItems();
    await nextTick(() => {
      scrollToBottom();
    });

    contacts.value = Array.from(messageItemsMap.value.values());
    sortContacts();
  });
});

//disconnect (memory leaks)
onUnmounted(() => {
  if(socket.value){
    socket.value.disconnect();
  }
});

async function fetchMessageItems(){
  try{
    const response = await fetch(`${API_BASE}/messageItems/${senderId}`);
    if(!response.ok) {
      throw new Error('Failed to fetch message items');
    }
    const data = await response.json();

    data.forEach((item) => {
      messageItemsMap.value.set(item.userId, item);
    });

    contacts.value = Array.from(messageItemsMap.value.values());

  }catch (error){
    console.error('Error fetching message items:', error);
  }
}

async function updateMessageItems(){
  try{
    const response = await fetch(`${API_BASE}/messageItems/${senderId}`, {
      method: 'POST',
      headers: {
        'Content-Type' : 'application/json',
      },
      body: JSON.stringify(Array.from(messageItemsMap.value.entries())),
    });
    if(!response.ok){
      throw new Error(`Failed to update message items: ${response.status} ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error updating message items:', error);
  }
}

async function sendMessage() {
  if (!messageInput.value || messageInput.value.trim() === '') return;
  
  if (!recipientId) {
    console.error("No recipient selected");
    return;
  }

  const newMessage = {
    sender_id: senderId,
    recipient_id: recipientId,
    message: messageInput.value,
  };

  try {
    const res = await fetch(`${API_BASE}/messages/send`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(newMessage)
    });

    const saved = await res.json();

    messages.value.push({
      sender_id: saved.sender_id,
      recipient_id: saved.recipient_id,
      message: saved.message,
      timestamp: saved.timestamp,
      message_id: saved.message_id,
      isSentByCurrentUser: true
    });

    await fetchMessageItems();
    await nextTick(() => scrollToBottom());

    contacts.value = Array.from(messageItemsMap.value.values());
    sortContacts();

  } catch (err) {
    console.error("ERROR sending message:", err);
  }

  messageInput.value = '';
}

function selectRecipient(userId, name) {
  recipientId = userId;
  currentRecipientId.value = userId;
  currentRecipientName.value = name;
  // clear the unread count when viewing the chat
  if (messageItemsMap.value.has(userId)) {
    messageItemsMap.value.get(userId).unreadCount = 0;
    updateMessageItems(); // persist change
  }
  isActive(currentRecipientId.value);
  loadMessageHistory();
}

async function loadMessageHistory() {
  if (!senderId || !recipientId) {
    messages.value = [];
    return;
  }

  const res = await fetch(`${API_BASE}/messages/${senderId}/${recipientId}`);
  const data = await res.json();

  messages.value = data.map((msg) => {
    return {
      ...msg,
      isSentByCurrentUser: String(msg.sender_id) === String(senderId)
    };
  });
  
  await nextTick(() => scrollToBottom());
}

function sortContacts() {
  contacts.value = contacts.value
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.split(" ");
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}


const questions = reactive([
  {
    title: 'What is a session?',
    answer: 'A virtual lesson is a digital space to communicate with your advisor over video call. You can upload documents to edit papers in real time, and pick our advisors brains to learn from their expertise.',
    isExpanded: false
  },
  {
    title: 'How do I begin a session?',
    answer: '1. Buy hours, or make sure you have enough remaining. \n\n2. If your advisor is currently online, click the "Start Session" button. If they are offline, you can always message them to find a time that works best!',
    isExpanded: false
  },
  {
    title: 'Who are CollegeGenius advisors?',
    answer: 'Our advisors are current college students who attend top 50 universities like Stanford, MIT, and Harvard. They have been vetted and trained to help you with anything!',
    isExpanded: false
  }
])

function handleAccordion(selectedIndex) {
  questions.forEach((_, index) => {
    questions[index].isExpanded = index === selectedIndex ? !questions[index].isExpanded : false
  })
}

function scrollToBottom() {
  if (messageScrollableArea.value) {
    messageScrollableArea.value.scrollTop = messageScrollableArea.value.scrollHeight;
  }
}

function isActive(id) {
  return String(id) === String(currentRecipientId.value);
}
</script>

<template>
  <NavBar/>
  <div class="page-container">
    <div class="white-box">
      <div class="messages-list">
        <h2> MY MESSAGES</h2>
        <div 
          v-for="(contact, index) in contacts" 
          :key="index" 
          @click="selectRecipient(contact.userId, contact.name)"
          class="message-item"
          :class="{ active: isActive(contact.userId) }"
        >
          <!-- <img :src="contact.profilePic" :alt="contact.name" class="profile-img" /> -->
          <div class="avatar">
            <img 
              v-if="contact.profilePic" 
              :src="contact.profilePic" 
              :alt="contact.name"
            />

            <div v-else class="avatar-initials">
              {{ getInitials(contact.name) }}
            </div>
          </div>

          <div class="recieved-message">
            <div class="name-of-message-sender">
              <span>{{ contact.name }}</span>

              <!-- Show unread badge only when > 0 -->
              <span 
                v-if="contact.unreadCount > 0" 
                class="unread-badge"
              >
                {{ contact.unreadCount }}
              </span>
            </div>
            <p class="last-message-preview">{{ contact.lastMessage }}</p>
          </div>
        </div>
      </div>
      <div class="message-details">
        <div class="main-chat-header">
          <h2>{{ currentRecipientName }}</h2>
        </div>
        <div class="message-scrollable-area" ref="messageScrollableArea">
          <div 
            v-for="message in messages" 
            :key="message.message_id || message.timestamp"
            class="message-row"
            :class="{
              'sent': message.isSentByCurrentUser,
              'received': !message.isSentByCurrentUser
            }"
          >
            <div class="message-bubble">
              {{ message.message }}
            </div>
          </div>
        </div>
        <div class="message-bar">
          <textarea 
            v-model="messageInput"
            placeholder="Type a message…"
            @keydown.enter.prevent="sendMessage"
            class="message-input"
          ></textarea>

          <button class="send-btn" @click="sendMessage">
            ➤
          </button>
        </div>

      </div>
      <div class="faq-sidebar">
        <div class="faq-header">
          <h2>FAQs</h2>
        </div>
        <div class="faq-question">
          <div v-for="(question, index) in questions" :key="question.title" class="faq-item">
            <button @click="() => handleAccordion(index)">
              {{ question.title }}
            </button>
            <Collapse :when="questions[index].isExpanded" class="collapse">
              <div class="faq-answer-box">
                <p>{{ question.answer }}</p>
              </div>
            </Collapse>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<style scoped>
/* -------------------------------
        GENERAL LAYOUT
-------------------------------- */
.page-container{
  padding-top: 8.5em;
  padding-bottom: 4em;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #dbeafe, #eff6ff);
}

.white-box{
  background-color: #fff;
  padding: 12px;
  width: 90%;
  max-width: 1300px;
  border-radius: 14px;
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: row;
  height: 38em;
  gap: 12px;
}

/* -------------------------------
        CONTACT LIST
-------------------------------- */
.messages-list{
  width: 280px;
  padding: 15px;
  overflow-y: auto;
  background-color: #f8fafc;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
}

.message-item{
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px 14px;
  border-radius: 14px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #ffffff;
  border: 1px solid #e5e7eb;
}

.message-item:hover{
  background-color: #eef2ff;
  border-color: #c7d2fe;
}

.message-item.active {
  background-color: #e0e7ff;
  border-color: #597ffd;
}

/* -------------------------------
        AVATAR UI
-------------------------------- */
.avatar{
  width: 46px;
  height: 46px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar img{
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initials{
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #597ffd;
  color: white;
  font-weight: 700;
  font-size: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* -------------------------------
        CHAT AREA
-------------------------------- */
.message-details{
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background-color: #fff;
}

/* HEADER */
.main-chat-header{
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
  font-weight: 600;
  text-align: center;
  border-radius: 14px 14px 0 0;
}

/* SCROLLABLE CHAT AREA */
.message-scrollable-area{
  flex-grow: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* MESSAGE BUBBLES */
.message-row{
  display: flex;
}

.sent{
  justify-content: flex-end;
}

.received{
  justify-content: flex-start;
}

.message-bubble{
  max-width: 60%;
  padding: 12px 16px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.35;
  box-shadow: 0 2px 5px rgba(0,0,0,0.06);
}

.sent .message-bubble{
  background: #c7d2fe;
  border-bottom-right-radius: 6px;
  color: #1e1b4b;
}

.received .message-bubble{
  background: #e5e7eb;
  border-bottom-left-radius: 6px;
  color: #1f2937;
}

/* -------------------------------
        MESSAGE INPUT BAR
-------------------------------- */
.message-bar{
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: #f9fafb;
  border-top: 1px solid #e5e7eb;
  gap: 10px;
  border-radius: 0 0 14px 14px;
}

.message-input{
  flex-grow: 1;
  min-height: 44px;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 22px;
  resize: none;
  font-size: 14px;
  background:#fff;
}

.message-input:focus{
  outline: none;
  border-color: #597ffd;
  box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.3);
}

.send-btn{
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #0040ff;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  transition: 0.2s ease;
  display:flex;
  align-items:center;
  justify-content:center;
}

.send-btn:hover{
  background: #021e72;
}


.faq-sidebar{
  width: 300px;
  background-color: #f0f8ff;
  padding: 20px;
  border-radius: 10px;
}

.faq-question {
  margin-bottom: 10px;
}

.faq-header{
  margin-bottom: 20px;
}

.collapse {
  transition: height 600ms cubic-bezier(0.3, 0, 0.6, 1);
}

.collapse[data-collapse="expanded"] {
  display: block;
}

.faq-item{
  margin-bottom: 10px;
}

.faq-item button{
  font-size: 1.1rem;
  width: 100%;
  text-align: center;
  padding: 8px 10px;
  border: none;
  cursor: pointer;
  background-color: #007bff;
}

.faq-answer-box{
  background-color: #f0f0f0;
  padding: 10px;
  border-radius: 5px;
  margin-top: 5px;
  text-align: center;
}

.faq-answer-box p{
  white-space: pre-line;
}


.name-of-message-sender {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-weight: 600;
  margin-bottom: 4px;
}

/* BLUE unread bubble */
.unread-badge {
  background-color: #0040ff; /* matches your Send button */
  color: white;
  padding: 2px 8px;
  font-size: 12px;
  border-radius: 12px;
  min-width: 20px;
  text-align: center;
  font-weight: 700;
}

</style>